import { ICoursesInPerson } from '../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { Teacher } from '../Teacher/types'

export default interface IFilter {
  search: string
  date: {
    start: string
    end: string
  }
  page_size: number
  page: number
}

export interface IContestApplicationsFilter {
  contest_name: string
  payment_status: string
  applicant_name: string
  page: number
  page_size: number
}

// Where the frame value of a contest comes from (ABRA-191).
// 'disabled' = the contest does not sell frames
// 'per_art'  = the applicant informs the value when submitting the artwork
// 'fixed'    = the contest defines a single value for every participant
export type TFrameValueMode = 'disabled' | 'per_art' | 'fixed'

/** ABRA commission on a sold artwork: a percentage of the sale or a fixed amount in reais. */
export type TCommissionType = 'percent' | 'fixed'

// Prefix of the contest public link on the website (ABRA-344): /concurso/{slug} or /exposicao/{slug}
export type TContestLinkType = 'concurso' | 'exposicao'

export const CONTEST_LINK_TYPE_LABELS: Record<TContestLinkType, string> = {
  concurso: 'Concurso',
  exposicao: 'Exposição'
}

// How a manual application (ABRA-398) was paid at the reception desk
export type TManualPaymentType = 'cash' | 'pix' | 'debit_card' | 'credit_card'

export const MANUAL_PAYMENT_TYPE_LABELS: Record<TManualPaymentType, string> = {
  cash: 'Dinheiro',
  pix: 'Pix',
  debit_card: 'Cartão de débito',
  credit_card: 'Cartão de crédito'
}

// Customizable artwork data fields
export type IArtFieldType = 'text' | 'textarea' | 'number' | 'select' | 'radio' | 'checkbox'

export interface IArtField {
  key: string
  label: string
  type: IArtFieldType
  icon?: string
  placeholder?: string
  options: string[]
  required: boolean
  standard: boolean
  // Per-location display targets (ABRA-73). Caps: listing 2, identifier 4, labels 2.
  show_in_listing?: boolean
  show_in_identifier?: boolean
  show_in_labels?: boolean
}

/**
 * Curated LineIcons (v4) options for the icon shown next to each question.
 * Both the panel and the public site load the same LineIcons stylesheet, so
 * these classes render identically in both places. Empty value = automatic.
 */
export const ART_FIELD_ICON_OPTIONS: { value: string; label: string }[] = [
  { value: 'lni lni-pencil', label: 'Lápis' },
  { value: 'lni lni-brush', label: 'Pincel' },
  { value: 'lni lni-brush-alt', label: 'Pincel (alternativo)' },
  { value: 'lni lni-ruler', label: 'Régua' },
  { value: 'lni lni-image', label: 'Imagem' },
  { value: 'lni lni-camera', label: 'Câmera' },
  { value: 'lni lni-tag', label: 'Etiqueta' },
  { value: 'lni lni-list', label: 'Lista' },
  { value: 'lni lni-text-format', label: 'Texto' },
  { value: 'lni lni-clipboard', label: 'Prancheta' },
  { value: 'lni lni-postcard', label: 'Cartão' },
  { value: 'lni lni-checkmark-circle', label: 'Confirmação' },
  { value: 'lni lni-user', label: 'Pessoa' },
  { value: 'lni lni-graduation', label: 'Formação' },
  { value: 'lni lni-coin', label: 'Moeda / Valor' },
  { value: 'lni lni-world', label: 'Mundo' },
  { value: 'lni lni-bolt-alt', label: 'Destaque' }
]

export interface IArtFieldAnswer {
  key: string
  label: string
  type: IArtFieldType
  value: string | null
  show_in_listing?: boolean
  show_in_identifier?: boolean
  show_in_labels?: boolean
}

/** Default schema mirroring the backend (Contest::defaultArtFields). */
// The title ("name") is a fixed field handled outside the configurable schema.
export const DEFAULT_ART_FIELDS: IArtField[] = [
  {
    key: 'technique',
    label: 'Técnica e suporte',
    type: 'text',
    icon: 'lni lni-brush',
    placeholder: 'Ex: Óleo sobre tela, técnica mista',
    options: [],
    required: true,
    standard: true,
    show_in_listing: true,
    show_in_identifier: true,
    show_in_labels: true
  },
  {
    key: 'dimensions',
    label: 'Dimensões',
    type: 'text',
    icon: 'lni lni-ruler',
    placeholder: 'Ex: A3, 30x40, 100x90, etc.',
    options: [],
    required: true,
    standard: true,
    show_in_listing: true,
    show_in_identifier: true,
    show_in_labels: true
  }
]

export interface IContestApplication {
  id: number
  contest_name: string
  applicant_name: string
  applicant_image_path: string
  applicant_cpf: string
  art_submissions_count: number
  payment_status: string
  is_free: boolean
  gateway_invoice_id: string | null
  /** Registered by staff in the panel (ABRA-398) */
  is_manual: boolean
  created_at: string
}

export interface IContestApplicationComplete {
  id: number
  applicant_id: number
  contest_id: number
  course_id: number
  teacher_id: number
  payment_status: string
  created_at: string
  updated_at: string
  /** Staff member who registered a manual application (ABRA-398); null for the site's own */
  created_by: { id: number; name: string } | null
  payment: {
    method: string
    /** Decimal cast: may arrive as a numeric string */
    amount: number | string
    paid_at: string | null
    manual_payment_type: TManualPaymentType | null
    notes: string | null
  } | null
  teacher: {
    id: number
    name: string
  }
  course: {
    id: number
    name: string
  }
  contest: {
    id: number
    name: string
    slug: string
    registration_deadline: string
    submission_deadline: string
    delivery_deadline: string
    opening_event_date: string
    contest_rules: string
    registration_fee: string
    max_registrations: number
    created_at: string
    updated_at: string
  }
  applicant: {
    id: number
    name: string
    email: string
    phone: string
    cpf: string
    instagram: string
    image_path: string
    image_url: string | null
    created_at: string
    updated_at: string
    bank_detail_has_info: boolean
    bank_detail_name: string | null
    bank_detail_cpf_cnpj: string | null
    bank_detail_code: string | null
    bank_detail_full_name: string | null
    bank_detail_agency: string | null
    bank_detail_account: string | null
    bank_detail_pix: string | null
    geo_ip?: string | null
    geo_city?: string | null
    geo_state?: string | null
    geo_latitude?: number | null
    geo_longitude?: number | null
    geo_has_info?: boolean
  }
  arts: [
    {
      id: number
      contest_application_id: number
      name: string
      technique: string
      dimensions: string
      field_answers?: IArtFieldAnswer[]
      work_type?: string | null
      work_description?: string | null
      available_for_auction: number
      starting_bid: number
      exhibition_status: string
      art_image_path: string
      art_image_url: string
      created_at: string
      updated_at: string
    }
  ]
}

export interface IContestsStorePayload {
  name: string
  slug: string
  link_type?: TContestLinkType
  registration_deadline: string
  submission_deadline: string
  delivery_deadline: string
  opening_event_date: string
  withdraw_start_date: string
  withdraw_end_date: string
  bid_increment?: number | null
  /** ABRA-337 — Fixed starting bid of every artwork up for auction; null lets the applicant choose. */
  default_starting_bid?: number | null
  commission_type?: TCommissionType
  commission_value?: number | null
  /** Late-withdrawal fine + daily storage fee in the delivery terms and the artwork receipt. */
  charge_storage_fee?: boolean
  auction_whatsapp_notifications?: boolean
  contest_rules: string
  registration_fee: number
  max_installments: number
  max_registrations: number
  min_arts_per_application?: number | null
  max_arts_per_application?: number | null
  courses_ids: number[]
  teachers_ids: number[]
  event_id: number
  contest_participants_ticket_id: number
  registration_fee_items?: string[]
  guest_ticket_id?: number | null
  whatsapp_group_link?: string | null
  art_fields?: IArtField[]
  art_fields_notice?: string | null
  use_work_type?: boolean
  /** ABRA-352 — kids contest: the site registration form asks for the student's data. */
  is_kids?: boolean
  frame_value_mode?: TFrameValueMode
  frame_value?: number | null
}

export interface IContestsUpdatePayload {
  name?: string
  slug?: string
  link_type?: TContestLinkType
  registration_deadline?: string
  submission_deadline?: string
  delivery_deadline?: string
  opening_event_date?: string
  withdraw_start_date?: string
  withdraw_end_date?: string
  auction_ends_at?: string | null
  bid_increment?: number | null
  /** ABRA-337 — Fixed starting bid of every artwork up for auction; null lets the applicant choose. */
  default_starting_bid?: number | null
  commission_type?: TCommissionType
  commission_value?: number | null
  /** Late-withdrawal fine + daily storage fee in the delivery terms and the artwork receipt. */
  charge_storage_fee?: boolean
  auction_whatsapp_notifications?: boolean
  contest_rules?: string
  registration_fee?: number
  max_installments?: number
  max_registrations?: number
  min_arts_per_application?: number | null
  max_arts_per_application?: number | null
  courses_ids?: number[]
  teachers_ids?: number[]
  event_id?: number
  contest_participants_ticket_id?: number
  registration_fee_items?: string[]
  guest_ticket_id?: number | null
  whatsapp_group_link?: string | null
  art_fields?: IArtField[]
  art_fields_notice?: string | null
  use_work_type?: boolean
  /** ABRA-352 — kids contest: the site registration form asks for the student's data. */
  is_kids?: boolean
  frame_value_mode?: TFrameValueMode
  frame_value?: number | null
}

export interface IContest {
  id: number
  name: string
  slug: string
  link_type: TContestLinkType
  registration_deadline: string
  submission_deadline: string
  delivery_deadline: string
  opening_event_date: string
  withdraw_start_date?: string
  withdraw_end_date?: string
  auction_ends_at?: string | null
  bid_increment?: number | null
  /** ABRA-337 — Decimal string from the API ("250.00"); null lets the applicant choose. */
  default_starting_bid?: number | string | null
  commission_type?: TCommissionType
  /** Decimal string from the API ("40.00"). */
  commission_value?: number | string | null
  charge_storage_fee?: boolean
  auction_whatsapp_notifications?: boolean
  contest_rules: string
  registration_fee: number
  max_installments: number
  max_registrations: number
  min_arts_per_application?: number | null
  max_arts_per_application?: number | null
  paid_applications_count: number
  applications_count: number
  created_at: string
  updated_at: string
  event_id?: number
  contest_participants_ticket_id?: number
  registration_fee_items?: string[]
  guest_ticket_id?: number | null
  whatsapp_group_link?: string | null
  art_fields?: IArtField[] | null
  art_fields_schema?: IArtField[]
  art_fields_notice?: string | null
  show_in_gallery: boolean
  use_work_type: boolean
  is_kids: boolean
  frame_value_mode: TFrameValueMode
  frame_value: number | null
  /** ABRA-338 — some artwork already has a bid, so the API refuses changing the fee. */
  has_auction_bids?: boolean
  /** Custom backgrounds of the public registration/submission steps (ABRA-336) and options screen (ABRA-348). */
  registration_background_url?: string | null
  submission_background_url?: string | null
  menu_background_url?: string | null
}

/** Multipart body of `POST /contests/:id/backgrounds`. A file replaces the step's background; a flag removes it. */
export interface IContestBackgroundsPayload {
  registration_background?: File
  submission_background?: File
  menu_background?: File
  remove_registration_background?: boolean
  remove_submission_background?: boolean
  remove_menu_background?: boolean
}

export interface IContestComplete extends IContest {
  registration_fee_values: IRegistrationFeeValues
  courses: ICoursesInPerson[]
  teachers: Teacher[]
}

export interface IContestDetail extends IContest {
  pending_applications_count: number
  total_revenue: number
  approved_arts_count: number
  arts_for_auction_count: number
  registrations_per_day: { date: string; count: number }[]
  courses: ICoursesInPerson[]
  teachers: Teacher[]
  event: { id: number; name: string; slug: string; thumb_wide: string | null } | null
}

export interface IContestApprovedArtwork {
  id: number
  name: string
  technique: string
  dimensions: string
  field_answers?: IArtFieldAnswer[]
  available_for_auction: boolean
  starting_bid: string
  exhibition_status: 'approved' | 'rejected'
  sale_status: 'available' | 'sold' | 'unsold'
  art_image_url: string | null
  applicant_name: string | null
  applicant_image_url: string | null
}

export interface IContestForSelect {
  id: number
  name: string
  slug: string
}

export interface IPickupEmailsStats {
  withdraw_start_date: string | null
  withdraw_end_date: string | null
  artworks_in_stock: number
  already_sent: number
  artworks_to_notify: number
  buyers: number
  applicants: number
  missing_email: number
  recipients: number
}

/** ABRA-339 — who would get the exhibitor certificate (counted per person, by e-mail). */
export interface IExhibitorCertificatesStats {
  participants: number // People with a paid application and at least one artwork exhibited (with or without e-mail)
  recipients: number // People the next dispatch would e-mail
  already_sent: number // People who already got it (skipped unless resending)
  missing_email: number // Eligible people without an e-mail (never sent)
}

export interface IExhibitorCertificatesPayload {
  subject: string
  body: string
  certificate_date: string // YYYY-MM-DD, printed on the certificate
  force: boolean
}

/** Body of `POST /contests/:id/applications/manual` (ABRA-398) */
export interface IContestManualApplicationPayload {
  name: string
  email: string
  phone: string
  cpf: string
  course_id: number
  teacher_id: number
  amount: number
  payment_type: TManualPaymentType
  paid_at: string // YYYY-MM-DD, not after today
  notes: string | null
}

export interface IContestManualApplicationResponse {
  message: string
  data: { id: number; uuid: string }
  /** Saved and paid, but the ticket e-mail / guest coupon failed to go out */
  warning?: string
}

export interface IContestRegistrationPayload {
  profile_image: File | null
  name: string
  email: string
  email_confirmation: string
  phone: string
  phone_confirmation: string
  cpf: string
  instagram: string

  course_id: number
  teacher_id: number

  rules_accepted: boolean

  payment_method: 'credit_card' | 'pix'

  credit_card: {
    number: string
    name: string
    expiration_month: string
    expiration_year: string
    cvv: string
    installments: number
  }
}

export interface IArt {
  art_image_url: string
  art_image: File | null
  name: string
  technique: string
  dimensions: string
  available_for_auction: boolean
  starting_bid: number
}

export interface IContestArtwork {
  id: number
  name: string
  technique: string
  dimensions: string
  field_answers?: IArtFieldAnswer[]
  work_type?: string | null
  work_description?: string | null
  available_for_auction: boolean
  starting_bid: number
  frame_value: number | null // Per-artwork override; null means it inherits the contest value
  effective_frame_value: number | null // What actually applies (override ?? contest value)
  frame_value_mode: TFrameValueMode // The contest's mode, needed because the listing spans contests
  exhibition_status: 'pending' | 'approved' | 'rejected' | 'stand_by'
  sale_status: 'available' | 'sold' | 'unsold'
  receipt_date: string | null
  return_date: string | null
  withdraw_start_date: string | null // First day of the contest's withdrawal window
  withdraw_end_date: string | null // Last fee-free day of the contest's withdrawal window
  delivery_status: 'pending' | 'received' | 'returned' | 'overdue'
  art_image_url: string | null
  applicant_name: string | null
  applicant_image_path: string | null
  contest_name: string | null
  contest_application_id: number
  created_at: string
  withdraw_days_late: number
  withdraw_storage_fee: number
  pickup_email_sent_at: string | null // When the pickup notice was emailed
}

export interface IContestArtworksFilter {
  contest_name: string
  applicant_name: string
  exhibition_status: string
  delivery_status: string
  sale_status: string
  available_for_auction: string
  pickup_email_status: string
  page: number
  page_size: number
}

export interface IRegistrationFeeValue {
  total: number
  installmentValue: number
}

export interface IRegistrationFeeValues {
  installments: { [key: number]: IRegistrationFeeValue }
  max_installments: number
  min_installment_value: number
}

export interface IContestApplicationWithArts {
  id: number
  applicant_name: string
  applicant_image_url: string
  email_notification_status: 'not_sent' | 'pending' | 'sending' | 'sent' | 'failed'
  teacher: {
    id: number
    name: string
  }
  course: {
    id: number
    name: string
  }
  art_submissions: {
    id: number
    name: string
    technique: string
    dimensions: string
    field_answers?: IArtFieldAnswer[]
    work_type?: string | null
    work_description?: string | null
    available_for_auction: boolean
    starting_bid: number
    art_image_url: string
    exhibition_status: 'pending' | 'approved' | 'rejected' | 'stand_by'
  }[]
}

export type ContestApplicationPaymentStatus =
  'paid' | 'pending' | 'declined' | 'cancelled' | 'cancel_pending' | 'refund_pending' | 'refunded'

export type CancelContestApplicationResponse = {
  message: string
  payment_status: ContestApplicationPaymentStatus
}

export interface IContestEvaluator {
  id: number
  contest_id?: number
  name: string
}

// ABRA-74 — criteria are now defined per contest (name + weight as a percentage).
export interface IContestEvaluationCriterion {
  id: number
  contest_id?: number
  name: string
  weight: number
  position: number
}

export interface IArtCriterionGrade {
  contest_evaluation_criterion_id: number
  grade: number | null
}

export interface IArtEvaluationByEvaluator {
  id: number
  contest_evaluator_id: number
  evaluator_name: string | null
  criterion_grades: IArtCriterionGrade[]
  grade_average: number | null
}

export interface IContestArtworkForEvaluation {
  id: number
  name: string
  technique: string
  dimensions: string
  field_answers?: IArtFieldAnswer[]
  work_type?: string | null
  work_description?: string | null
  exhibition_status: 'pending' | 'approved' | 'rejected' | 'stand_by'
  evaluation_status: 'evaluated' | 'pending'
  created_at: string
  art_image_url: string | null
  applicant_name: string | null
  applicant_id: number | null
  contest_name: string | null
  contest_id: number | null
  overall_average: number | null
  /**
   * ABRA-314 — a curadoria da vitrine do site, resolvida no backend.
   *
   * `showcase_override` é de três estados: `null` segue a regra automática,
   * `true` força a obra na vitrine e `false` a tira de lá. `showcase_auto` é o
   * que a regra diz sozinha e `showcase_selected` é o resultado dos dois — o
   * painel exibe o segundo e decide o próximo override pelo primeiro, em vez de
   * reimplementar a regra aqui.
   */
  showcase_override: boolean | null
  showcase_position: number | null
  showcase_auto: boolean
  showcase_selected: boolean
  evaluations: IArtEvaluationByEvaluator[]
}

/**
 * Um slot da vitrine já tomado, e por qual obra.
 *
 * Vem para o concurso inteiro, não para a página que está na tela: com 12 obras
 * por página e 20 slots, quem tem o 3º normalmente está em outra página, e um
 * select que só conhecesse a página atual seguiria oferecendo um slot ocupado.
 */
/**
 * Quantos slots fixos a vitrine do site oferece (ABRA-314).
 *
 * O número real vem na listagem (`showcase_max_position`), que é o mesmo do
 * `max:` que o endpoint de curadoria valida. Este aqui é só o valor inicial,
 * antes da primeira resposta, para o select não nascer vazio.
 */
export const DEFAULT_MAX_SHOWCASE_POSITION = 20

export interface IShowcaseTakenPosition {
  position: number
  artwork_id: number
  artwork_name: string
}

/** O que o painel manda ao curar a vitrine de uma obra. */
export interface IArtworkShowcasePayload {
  showcase_override: boolean | null
  showcase_position: number | null
}

/** O estado da vitrine devolvido pelo backend depois de curar uma obra. */
export interface IArtworkShowcaseState extends IArtworkShowcasePayload {
  id: number
  showcase_auto: boolean
  showcase_selected: boolean
  /** Já atualizada, para o painel repintar os selects sem refazer a listagem. */
  taken_positions: IShowcaseTakenPosition[]
}

export interface IArtworkEvaluationFilter {
  contest_id: string
  applicant_id: string
  search: string
  exhibition_status: string
  evaluation_status: string
  /** `in` / `out` / `''` — se a obra está na vitrine do site (ABRA-314). */
  showcase: string
  order_by: string
  order_direction: string
  page: number
  page_size: number
}

export interface IApplicantSelectOption {
  id: number
  name: string
}

/**
 * Pickup person info (who retrieves the artwork)
 * Can be either the buyer (if artwork was sold) or the applicant (if not sold)
 */
export interface IPickupPerson {
  type: 'buyer' | 'applicant'
  name: string
  email: string
  cpf?: string
  phone?: string
  tab_code?: string
}

/**
 * Bank account the author entered at artwork submission, where the payout goes
 */
export interface IApplicantBankDetails {
  holder_name: string | null
  cpf_cnpj: string | null
  bank_code: string | null
  bank_name: string | null
  agency: string | null
  account: string | null
  pix_key: string | null
}

/**
 * Applicant/author info
 */
export interface IApplicant {
  id: number
  name: string
  email: string
  cpf: string
  phone: string
  course?: string
  /** Null when the author never filled the bank step. */
  bank_details: IApplicantBankDetails | null
}

/**
 * Payout details for an artwork sale (refinement #6)
 */
export interface IPayoutDetails {
  value_paid: number
  commission_type: TCommissionType
  commission_value: number
  /** Amount ABRA keeps from this sale, already capped at the sale value. */
  commission: number
  value_net: number
  paid_at: string | null
  paid_by: string | null
  payment_notes: string | null
}

/**
 * Payout/Pickup info for an artwork
 * Used by refinement #6 (payout) and #7 (pickup modal)
 */
export interface IPayoutInfo {
  art: {
    id: number
    name: string
    art_image_url?: string
    available_for_auction?: boolean
    sale_status: 'available' | 'sold' | 'unsold'
    sale_type: 'auction' | 'post_auction' | null
    // ABRA-191 — null means the contest sells no frame for this artwork, so
    // there is nothing to decide at pickup. When it has a value, whether the
    // frame goes with the artwork depends on frame_charged_amount.
    effective_frame_value: number | null
    // How much frame was actually billed for this artwork; null when the
    // attendant did not add it.
    frame_charged_amount: number | null
    buyer_stone_id: string | null
    buyer_payment_type: 'pix' | 'credit' | 'debit' | null
    buyer_receipt_url: string | null
  }
  applicant: IApplicant
  pickup_person: IPickupPerson
  payout: IPayoutDetails | null
  buyer_data_complete: boolean
}

export interface IArtEvaluationsBulkPayload {
  criterion_id: number
  grades: { evaluator_id: number; grade: number }[]
}

export interface IPostAuctionSalePayload {
  buyer_name: string
  buyer_email: string
  buyer_phone: string
  buyer_cpf: string
  payment_type: 'pix' | 'credit' | 'debit'
  sale_price?: number
  receipt?: File
}

export interface IEvaluatorsSyncPayload {
  evaluators: { id?: number; name: string }[]
}

export interface ICriteriaSyncPayload {
  criteria: { id?: number; name: string; weight: number }[]
}
